import {
  useState,
  useCallback,
  useEffect,
  useImperativeHandle,
  forwardRef,
  useRef,
} from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";
import { nawyTheme } from "@/styles/nawy-theme/nawy-theme";
import { AddTaskRequest, TasksResponse } from "@/modules/tasks/dtos";
import { addMultipleTasksRequest } from "@/modules/meetings/types";
import { useAddTaskForm } from "@/modules/tasks/hooks/add-task.hook";
import TaskFormFields from "@/modules/tasks/components/Add/task-form-fields";
import { combineDateAndTime } from "@/modules/tasks/utils";
import { addTaskSchema } from "@/modules/tasks/hooks/add-task/use-form";
import { handleMultipleTasks } from "@/modules/meetings/hooks/handle-multiple-tasks.hook";
import { showToast } from "@/modules/common/utils";
import { ToastType } from "@/modules/common/types";
import TaskAiEvaluationDisplay from "@/modules/common/components/task-ai-evaluation-display";
import { useTaskAiEvaluation } from "@/modules/common/hooks/task-ai-evaluation.hook";
import CustomLabel from "@/modules/common/components/input/custom-label";
import { buildTaskContentWithContext } from "@/modules/tasks/utils";
// ✅ Validate tasks using zod schema
const validateTask = (task: addMultipleTasksRequest): boolean => {
  try {
    const schema = addTaskSchema();
    schema.parse({
      title: task.title || "",
      type: task.type,
      userUId: task?.userUId,
      dueDate: task.dueDate,
      taskUId: task.taskUId,
      selectedTime: task.selectedTime,
      linkedEntities: task.linkedEntities || [],
    });
    return true;
  } catch {
    return false;
  }
};

interface MarkAsCompletedProps {
  meeting: TasksResponse;
  onTasksChange?: (tasks: AddTaskRequest[]) => void;
  onValidationChange?: (hasIncompleteTasks: boolean) => void;
  onTasksNeedEvaluation?: () => void;
  onTasksReadyForEvaluation?: (ready: boolean) => void;
}

export interface MarkAsCompletedRef {
  evaluateAllTasks: () => Promise<boolean>;
}

interface TaskWithEvaluation extends addMultipleTasksRequest {
  evaluation?: any | null;
}

export const MarkAsCompleted = forwardRef<
  MarkAsCompletedRef,
  MarkAsCompletedProps
>(
  (
    {
      meeting,
      onTasksChange,
      onValidationChange,
      onTasksNeedEvaluation,
      onTasksReadyForEvaluation,
    },
    ref
  ) => {
    const { form, setters, options, loading, handlers } = useAddTaskForm({
      alwaysOpen: true,
    });
    const { evaluateTaskContent } = useTaskAiEvaluation();

    const createInitialTask = (id: number): TaskWithEvaluation => ({
      id,
      title: "",
      type: undefined,
      userUId: meeting?.Participants?.[0]?.User?.uid || "",
      taskUId: meeting?.uid || "",
      dueDate: "",
      selectedTime: "",
      linkedEntities: [],
      evaluation: null,
    });

    const createInitialTaskForm = () => ({
      userUId: meeting?.Participants?.[0]?.User?.uid || "",
      type: undefined,
      dueDate: "",
      selectedTime: "",
      linkedEntities: [],
      selectedProperty: null,
      linkTo: null,
      linkToEntity: "",
    });

    const [tasks, setTasks] = useState<TaskWithEvaluation[]>(() => [
      createInitialTask(1),
    ]);

    const [taskForms, setTaskForms] = useState<{ [key: number]: any }>(() => ({
      [1]: createInitialTaskForm(),
    }));

    const [evaluatingTaskIds, setEvaluatingTaskIds] = useState<Set<number>>(
      new Set()
    );

    const taskRefs = useRef<{ [key: number]: HTMLDivElement | null }>({});

    const filteredOptions = {
      ...options,
      users: meeting?.Participants?.[0]?.User?.uid
        ? [
            {
              value: meeting.Participants?.[0]?.User?.uid,
              label: meeting.Participants?.[0]?.User?.name || "",
            },
          ]
        : options.users,
    };

    useEffect(() => {
      const userUId = meeting?.Participants?.[0]?.User?.uid;
      if (userUId) {
        form.setValue("userUId", userUId);
      }
    }, [meeting, form]);

    const updateFormDueDate = useCallback(
      (dateObj: Date | null, time: string) => {
        if (!dateObj) return "";
        const combinedDateTime = combineDateAndTime(dateObj, time);
        return combinedDateTime?.toISOString() || "";
      },
      []
    );

    const validateAndUpdateTasks = useCallback(
      (updatedTasks: TaskWithEvaluation[]) => {
        const hasIncomplete = updatedTasks.some((task) => !validateTask(task));
        onValidationChange?.(hasIncomplete);

        const validTasks = updatedTasks.filter((task) => validateTask(task));

        const tasksToSubmit: AddTaskRequest[] = validTasks.map((task) => ({
          title: task.title,
          type: task.type,
          userUId: task.userUId,
          taskUId: task.taskUId,
          dueDate: task.dueDate!,
          linkedEntities: task.linkedEntities || [],
        }));

        onTasksChange?.(tasksToSubmit);
        onTasksReadyForEvaluation?.(
          validTasks.length > 0 && validTasks.every((t) => t.title && t.dueDate)
        );
      },
      [onValidationChange, onTasksChange, onTasksReadyForEvaluation]
    );

    const handleTaskChange = useCallback(
      (id: number, field: string, value: any) => {
        setTaskForms((prev) => ({
          ...prev,
          [id]: {
            ...prev[id],
            [field]: value,
          },
        }));

        const currentTaskForm = taskForms[id] || {};
        const updatedTasks = tasks.map((task) => {
          if (task.id === id) {
            if (field === "title") {
              onTasksNeedEvaluation?.();
              return { ...task, [field]: value, evaluation: null };
            }
            if (field === "dueDate" || field === "selectedTime") {
              const currentDate =
                field === "dueDate" ? value : currentTaskForm.dueDate;
              const currentTime =
                field === "selectedTime" ? value : currentTaskForm.selectedTime;
              if (currentDate && currentTime) {
                const combinedDate = updateFormDueDate(
                  new Date(currentDate),
                  currentTime
                );
                return {
                  ...task,
                  dueDate: combinedDate,
                  selectedTime:
                    field === "selectedTime" ? value : task.selectedTime,
                };
              } else {
                return task;
              }
            }
            if (field === "selectedProperty") {
              return {
                ...task,
                linkedEntities: value ? [value] : [],
              };
            }
            if (field === "type" || field === "userUId") {
              form.setValue(field, value);
            }
            return { ...task, [field]: value };
          }
          return task;
        });

        setTasks(updatedTasks);
        validateAndUpdateTasks(updatedTasks);
      },
      [
        taskForms,
        tasks,
        updateFormDueDate,
        validateAndUpdateTasks,
        onTasksNeedEvaluation,
        form,
      ]
    );

    const handleAddTask = () => {
      const newTaskId = tasks.length + 1;
      const newTask: TaskWithEvaluation = createInitialTask(newTaskId);
      const updatedTasks = [...tasks, newTask];
      setTasks(updatedTasks);
      setTaskForms((prev) => ({
        ...prev,
        [newTaskId]: createInitialTaskForm(),
      }));
      validateAndUpdateTasks(updatedTasks);
      onTasksNeedEvaluation?.();

      // Scroll to the new task after it's rendered
      setTimeout(() => {
        if (taskRefs.current[newTaskId]) {
          taskRefs.current[newTaskId]?.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 100);
    };

    // ✅ AI Evaluation logic
    const performEvaluations = useCallback(
      async (tasksToEvaluate: TaskWithEvaluation[]) => {
        const evaluatingIds = new Set(tasksToEvaluate.map((t) => t.id));
        setEvaluatingTaskIds(evaluatingIds);
        const evaluationPromises = tasksToEvaluate.map((task) => {
          const contextMessage = buildTaskContentWithContext(
            task.title,
            task.linkedEntities
          );

          return evaluateTaskContent(contextMessage).then((result) => ({
            taskId: task.id,
            evaluation: result,
          }));
        });
        const results = await Promise.all(evaluationPromises);
        const evaluated = tasks.map((t) => {
          const r = results.find((x) => x.taskId === t.id);
          return r ? { ...t, evaluation: r.evaluation } : t;
        });
        setEvaluatingTaskIds(new Set());
        setTasks(evaluated);
        validateAndUpdateTasks(evaluated);
        return evaluated;
      },
      [tasks, evaluateTaskContent, validateAndUpdateTasks]
    );

    const evaluateAllTasks = useCallback(async (): Promise<boolean> => {
      const tasksToEval = tasks.filter((t) => t.title && !t.evaluation);
      if (!tasksToEval.length) {
        return tasks.every(
          (t) =>
            t.evaluation?.classification === "Actionable" ||
            t.evaluation?.classification === "Can Be Better"
        );
      }
      try {
        const evaluated = await performEvaluations(tasksToEval);
        return evaluated.every(
          (t) =>
            t.evaluation?.classification === "Actionable" ||
            t.evaluation?.classification === "Can Be Better"
        );
      } catch {
        showToast({
          type: ToastType.ERROR,
          message: "Failed to evaluate tasks",
          toastId: "taskEvaluationError",
        });
        return false;
      }
    }, [tasks, performEvaluations]);

    useImperativeHandle(ref, () => ({ evaluateAllTasks }), [evaluateAllTasks]);

    return (
      <Box
        sx={{
          padding: 2,
          width: "100%",
          maxHeight: "500px",
          overflowY: "auto",
        }}
      >
        {tasks.map((task) => (
          <Box
            key={task.id}
            ref={(el) => {
              if (el) {
                taskRefs.current[task.id] = el as HTMLDivElement;
              }
            }}
            sx={{ mb: 3, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Typography
              sx={{
                ...nawyTheme.nawy.typography.english.variants.body.md.semibold,
                color: nawyTheme.nawy.colors.primitives.grey[900],
              }}
            >
              Task {task.id}
            </Typography>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <CustomLabel
                  sx={{
                    ...nawyTheme.nawy.typography.english.variants.body.xs
                      .regular,
                    color: nawyTheme.nawy.colors.primitives.grey[600],
                  }}
                >
                  Task
                </CustomLabel>
                {evaluatingTaskIds.has(task.id) && (
                  <CircularProgress size={16} />
                )}
              </Box>
              <TextField
                fullWidth
                placeholder="Task"
                size="small"
                value={task.title}
                onChange={(e) =>
                  handleTaskChange(task.id, "title", e.target.value)
                }
                error={
                  !!(
                    task.evaluation &&
                    task.evaluation.classification !== "Actionable" &&
                    task.evaluation.classification !== "Can Be Better"
                  )
                }
                sx={{
                  width: "100%",
                  "& > .MuiBox-root:first-of-type": {
                    display: "none",
                  },
                  "& .MuiInputBase-root": {
                    height: "36px",
                    borderRadius: nawyTheme.nawy.radius.lg,
                    "& .MuiInputBase-input": {
                      fontSize: "1.0rem",
                      padding: "8px 14px",
                    },
                  },
                }}
                InputProps={{
                  sx: {
                    borderRadius: nawyTheme.nawy.radius.lg,
                  },
                }}
              />

              {task.evaluation && (
                <TaskAiEvaluationDisplay
                  evaluation={task.evaluation}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>
            <TaskFormFields
              control={form.control}
              errors={form.errors}
              state={{
                type: taskForms[task.id]?.type ?? task.type ?? "",
                dueDate:
                  taskForms[task.id]?.dueDate ??
                  (task.dueDate ? new Date(task.dueDate) : null),
                selectedTime: taskForms[task.id]?.selectedTime ?? "",
                selectedProperty: taskForms[task.id]?.selectedProperty ?? null,
                userUId: taskForms[task.id]?.userUId ?? task.userUId ?? "",
                linkTo: taskForms[task.id]?.linkTo ?? null,
                linkToEntity: taskForms[task.id]?.linkToEntity ?? "",
              }}
              setters={setters}
              options={filteredOptions}
              loading={loading}
              handlers={handleMultipleTasks(
                handlers,
                task.id,
                handleTaskChange,
                { setters }
              )}
            />
          </Box>
        ))}

        <Button
          onClick={handleAddTask}
          disabled={evaluatingTaskIds.size > 0}
          sx={{
            color:
              evaluatingTaskIds.size > 0
                ? nawyTheme.nawy.colors.primitives.grey[400]
                : nawyTheme.nawy.colors.primitives.blue[500],
            textTransform: "none",
            fontWeight: 600,
            pl: 0,
            alignSelf: "flex-start",
          }}
        >
          Add new task
        </Button>
      </Box>
    );
  }
);
